import * as React from 'react'
import { v4 as uuidv4 } from 'uuid'
import styled from 'styled-components'
import moment from 'moment'
import * as constants from 'static/constants'
import { jobService } from 'core/services'
import { IJob, IAgentRank, IWindow } from 'static/interfaces'
import { IBuildedTour } from 'utils/weeklyTypes'
import SvgIcon from 'components/atoms/SvgIcon'
import { StaticContext } from 'core/contexts/weekly/StaticContext'
import { useWeeklyContext } from 'core/contexts/weekly/WeeklyContext'
import Job from './Job'
import JobsDetailModalBody from './JobsDetailModalBody'
import { defaultJobSlot, getJobStatus } from 'utils/job'

declare var window: IWindow

interface IProps {
  tour: IBuildedTour
}

const Jobs: React.VFC<IProps> = ({ tour }) => {
  const { getTourSkills, getSkills } = React.useContext(StaticContext)
  const { refetchTour } = useWeeklyContext()
  const tourSkills = getTourSkills()
  // GlobalModal(WeeklyProviderツリー外)ではStaticContextが届かないため、モーダルへはpropsで渡す
  const skills = getSkills()
  const [jobs, setJobs] = React.useState<IJob[]>([])
  const [specialRanks, setSpecialRanks] = React.useState<IAgentRank[]>([])
  const [creating, setCreating] = React.useState<boolean>(false)

  React.useEffect(() => {
    jobService
      .getJobs({ tour_id: tour.id, is_deleted: false })
      .then(res => {
        setJobs(res.jobs)
        setSpecialRanks(res.agent_ranks)
      })
      .catch(err => {
        console.error(err)
      })
  }, [tour])

  const defaultJob = React.useMemo(() => {
    return {
      id: null,
      tour_id: tour.id,
      publish_start_at: moment()
        .startOf('day')
        .add(5, 'hours')
        .format('YYYY-MM-DD HH:mm'),
      publish_end_at: moment(tour.day)
        .startOf('day')
        .subtract(1, 'day')
        .add(20, 'hours')
        .format('YYYY-MM-DD HH:mm'),
      is_draft: true,
      job_bulk_plan_id: null,
      job_slots: [defaultJobSlot()],
      client_uid: uuidv4(),
    } as IJob
  }, [tour])

  // 実効状態(非公開は 個別 かつ is_draft のみ。それ以外は時刻による3値判定)
  const effectiveStatus = (job: IJob) => {
    const isBulk = job.job_bulk_plan_id && !job.has_bulk_diff
    return !isBulk && job.is_draft ? 'draft' : getJobStatus(job)
  }

  const statusCounts = React.useMemo(() => {
    const counts = { open: 0, upcoming: 0, ended: 0, draft: 0 }
    jobs.forEach(job => {
      counts[effectiveStatus(job)] += 1
    })
    return counts
  }, [jobs])

  // 直近の締切日時: 非公開を除き、募集中/募集予定があれば最小、全件終了なら最大のpublish_end_at
  const nearestPublishEndAt = React.useMemo(() => {
    const publishedJobs = jobs.filter(job => effectiveStatus(job) !== 'draft')
    if (publishedJobs.length === 0) {
      return null
    }
    const activeJobs = publishedJobs.filter(job => getJobStatus(job) !== 'ended')
    const targetJobs = activeJobs.length > 0 ? activeJobs : publishedJobs
    const endAts = targetJobs.map(job => moment(job.publish_end_at))
    return activeJobs.length > 0 ? moment.min(endAts) : moment.max(endAts)
  }, [jobs])

  const handleShowDetailModal = React.useCallback(() => {
    window.globalModal.showModal({
      small: true,
      body: (
        <JobsDetailModalBody
          tour={tour}
          tourSkills={tourSkills}
          skills={skills}
          refetchTour={refetchTour}
        />
      ),
    })
  }, [tour, tourSkills, skills, refetchTour])

  return (
    <Wrapper>
      {jobs.length >= 2 ? (
        <div className="jobsSummary">
          {statusCounts.open > 0 && (
            <div className="statusBadge open">募集中 {statusCounts.open}</div>
          )}
          {statusCounts.upcoming > 0 && (
            <div className="statusBadge upcoming">募集予定 {statusCounts.upcoming}</div>
          )}
          {statusCounts.ended > 0 && (
            <div className="statusBadge ended">募集終了 {statusCounts.ended}</div>
          )}
          {statusCounts.draft > 0 && (
            <div className="statusBadge draft">非公開 {statusCounts.draft}</div>
          )}
          {nearestPublishEndAt && (
            <div className="nearestPublishEndAt">
              直近の締め切り日時: {nearestPublishEndAt.format('YYYY/MM/DD, H:mm')}
            </div>
          )}
          <button type="button" className="detailButton" onClick={handleShowDetailModal}>
            詳細を確認・編集する
          </button>
        </div>
      ) : (
        jobs.map(job => (
          <Job
            key={job.id}
            tour={tour}
            job={job}
            tourSkills={tourSkills}
            specialRanks={specialRanks}
            skills={skills}
            setCreating={setCreating}
            isCreating={false}
            refetchTour={refetchTour}
          />
        ))
      )}
      {creating ? (
        <Job
          key={defaultJob.client_uid}
          tour={tour}
          job={defaultJob}
          tourSkills={tourSkills}
          specialRanks={specialRanks}
          skills={skills}
          setCreating={setCreating}
          isCreating={true}
          refetchTour={refetchTour}
        />
      ) : jobs.length === 0 ? (
        <div className="createButton">
          <button onClick={() => setCreating(true)} type="button">
            <SvgIcon name="person_search" className="icon" ariaHidden={true} role="presentation" />
            募集を新規作成する
          </button>
        </div>
      ) : null}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: fit-content;
  font-size: 13px;
  padding: 0;
  &:has(div.edit),
  &:has(div.open),
  &:has(div.upcoming),
  &:has(div.ended),
  &:has(div.jobsSummary) {
    width: 100%;
  }
  .jobsSummary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 12px;
    width: 100%;
    line-height: 2rem;
    padding: 4px;
    border-radius: 4px;
    background-color: ${constants.COLORS.infoBG2};
    .statusBadge {
      font-size: 12px;
      line-height: 16px;
      padding: 4px 8px;
      border-radius: 12px;
      &.open {
        color: ${constants.COLORS.white};
        background-color: ${constants.COLORS.info2};
      }
      &.upcoming {
        color: ${constants.COLORS.info2};
        background-color: ${constants.COLORS.white};
        border: 1px solid ${constants.COLORS.info2};
      }
      &.ended {
        color: ${constants.COLORS.subText};
        background-color: ${constants.COLORS.disabled};
      }
      &.draft {
        color: ${constants.COLORS.white};
        background-color: ${constants.COLORS.danger};
      }
    }
    .nearestPublishEndAt {
      font-size: 12px;
      color: ${constants.COLORS.black};
    }
    .detailButton {
      padding: 0;
      border: none;
      background-color: transparent;
      font-size: 12px;
      color: ${constants.COLORS.primary};
      cursor: pointer;
      transition: 0.2s color;
      :hover {
        color: ${constants.COLORS.secondary};
      }
    }
  }
  .createButton {
    button {
      display: flex;
      align-items: center;
      gap: 4px;
      line-height: 20px;
      color: ${constants.COLORS.primary};
      padding: 0;
      background-color: transparent;
      border: none;
      font-size: 12px;
      cursor: pointer;
      transition: 0.2s color;
      :hover {
        color: ${constants.COLORS.secondary};
      }
      .icon {
        width: 20px;
        height: 20px;
      }
    }
  }
`
export default Jobs
